import {useCallback, useEffect, useMemo, useState} from 'react';

import {businessCountryService} from '@/services/businessCountryService';
import type {AfriexQueryParams, BusinessCountry} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

const PAGE_SIZE = 10;

/**
 * List + detail + save for the B2B country catalogue, mirroring
 * `useCountryUpdate`.
 *
 * A sibling of that hook rather than a `target`-parameterised version of it:
 * the two read different endpoints, key on different fields (`code` vs `iso2`)
 * and edit disjoint field sets, so sharing would mean parameterising all three
 * for the paging boilerplate.
 *
 * Preloads the whole catalogue (the loop below) rather than paging 10 at a
 * time server-side, the same trade `useCurrencyUpdate` already makes: the
 * B2B country list is short, the endpoint has no `search`/`name` param to
 * offload filtering to, and a typed search term needs every row in memory to
 * actually find a country rather than only the currently visible page.
 */
const useBusinessCountryUpdate = () => {
  const [fullList, setFullList] = useState<BusinessCountry[]>([]);
  const [item, setItem] = useState<BusinessCountry>({} as BusinessCountry);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isPageReset, setIsPageReset] = useState<boolean>(false);

  const filteredList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return fullList;
    }
    return fullList.filter(
      (country) =>
        country.name?.toLowerCase().includes(term) ||
        country.code?.toLowerCase().includes(term),
    );
  }, [fullList, searchTerm]);

  const isSearch = Boolean(searchTerm.trim());

  const list = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, page]);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setPage(0);
    setIsPageReset((prev) => !prev);
  }, []);

  const fetchList = useCallback(async (params: AfriexQueryParams) => {
    try {
      setIsLoading(true);
      // Downloading the whole collection is deliberate here: there is one row
      // per business country (`code` is limited to AfriexCountryCode, well
      // under 150), so this loop runs once or twice and the screen can then
      // search and page the full list locally.
      //
      // Do not copy this loop for a collection that grows (transactions, fees,
      // users, audit events). Every page re-runs the server's count and a
      // longer skip, so each page costs more than the last, and the browser
      // ends up holding every row. For those, page on the server and render
      // one page at a time, stream the full range from one cursor (the
      // `?action=DOWNLOAD` exports built on streamCursorToConsumer), or, in a
      // server job, read in `_id` batches the way BatchTransactionProcessor
      // does.
      const allItemsMap = new Map<string, BusinessCountry>();
      let currentPageIndex = 0;
      let hasMore = true;
      let itemsTotal = 0;

      while (hasMore) {
        const response = await businessCountryService.getList({
          ...params,
          page: currentPageIndex,
          limit: 100,
        });

        const items = response.data || [];
        itemsTotal = response.total || 0;

        items.forEach((item) => {
          const key = item.id || item.code;
          allItemsMap.set(key, item);
        });

        if (
          allItemsMap.size >= itemsTotal ||
          items.length === 0 ||
          items.length < 100
        ) {
          hasMore = false;
        } else {
          currentPageIndex++;
        }
      }

      setFullList(Array.from(allItemsMap.values()));
      setPage(0);
    } catch (e) {
      showErrorMessage(
        `Fetching business country list failed:${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchItem = useCallback(async (countryCode: string) => {
    try {
      setIsLoading(true);
      setItem(await businessCountryService.getItem(countryCode));
    } catch (e) {
      showErrorMessage(
        `Fetching business country details failed:${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // `fetchList` try/catches into showErrorMessage, so this only fires if that
    // reporting itself threw. Attached so an empty catalogue cannot end up
    // looking like a loaded one; dev logger rather than a second toast, since a
    // toast is what just failed.
    fetchList({}).catch(logger.error);
  }, [fetchList]);

  const handleUpdate = useCallback(
    async (countryId: string, body: Partial<BusinessCountry>) => {
      if (!body) {
        return;
      }
      try {
        setIsLoading(true);
        const country = await businessCountryService.saveOne(countryId, body);
        if (country) {
          setItem(country);
          showSuccessMessage('Business country updated successfully');
        }
      } catch (e) {
        showErrorMessage(
          `Updating business country failed:${getErrorMessage(e, String(e))}`,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    list,
    item,
    isLoading,
    isSearch,
    isPageReset,
    fetchList,
    fetchItem,
    handleUpdate,
    pageInfo: {page, total: filteredList.length},
    setPage,
    searchTerm,
    handleSearch,
  };
};

export default useBusinessCountryUpdate;
